import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal } from './comps';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import MaterialInput from 'src/components/form/MaterialInput';
import { InputLabel, Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import { Plan } from 'src/api/types';
import { Guard } from './Guard';
import { RestrictAccessSelect, OrganisationSelect } from '../select';
import useOrgSwitch from 'src/hooks/useWorkspaceSwitch';
import { WorkspaceTypeSelect } from '../select/WorkspaceTypeSelect';
import AutoGrowingInput from '../form/AutoGrowingInput';
import { useCache } from 'src/graphql/useCache';
import { useUpsertWorkspaceMutation } from 'src/graphql/_generated';

const StyledModal = styled(Modal)`
  width: 566px;
  padding: 0 !important;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .btns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 20px;
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }
`;

interface WorkspaceCreateModalProps {
  onClose: () => void;
}

export const WorkspaceCreateModal: React.FC<WorkspaceCreateModalProps> = ({ onClose }) => {
  const [err, setErr] = useState('');
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const { refetchQuery } = useCache();

  const { isLoading: isLoadingSwitch } = useOrgSwitch({
    onError: (err) => setErr(err.message || 'Something went wrong'),
  });

  const validationSchema = Yup.object({
    name: Yup.string().required('Required').max(200, 'Max 200 characters'),
    primaryContact: Yup.string().required('Required').email('Invalid email address').max(100, 'Max 100 characters'),
    organisationId: Yup.string().required('Required'),
    plan: Yup.string().required('Required'),
    isRestricted: Yup.boolean().required('Required'),
    currency: Yup.string()
      .max(3, 'Currency must be at most 3 characters long')
      .matches(/^[^\s]*$/, 'Currency must not contain spaces')
      .nullable(),
  });

  const [create] = useUpsertWorkspaceMutation({
    onCompleted: () => {
      refetchQuery({ include: ['profile', 'flatOrganisations'] });
      onClose();
    },
    onError: (err: any) => setErr(err.message || 'Something went wrong'),
  });

  const {
    values,
    handleBlur,
    handleChange,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    isSubmitting,
    handleSubmit,
    isValid,
    dirty,
  } = useFormik({
    initialValues: {
      name: '',
      primaryContact: '',
      plan: '' as Plan,
      organisationId: '',
      isRestricted: true,
      currency: undefined,
    },
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      return create({ variables: { ...values, isRestricted: !!values.isRestricted } });
    },
  });

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen>
          <Spacer $px={40} />
          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              Create a Workspace
            </Poppins>
            <Spacer $px={30} />
            <InputLabel>WORKSPACE NAME *</InputLabel>
            <AutoGrowingInput
              name="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder="Workspace name"
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              fullWidth
            />
            <Spacer $px={20} />
            <InputLabel>PRIMARY CONTACT *</InputLabel>
            <AutoGrowingInput
              type="email"
              name="primaryContact"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.primaryContact}
              placeholder="Primary contact email address"
              error={touched.primaryContact && Boolean(errors.primaryContact)}
              helperText={touched.primaryContact && errors.primaryContact}
              fullWidth
            />
            <Spacer $px={20} />
            <InputLabel>PARENT ORGANISATION *</InputLabel>
            <OrganisationSelect
              placeholder="Parent organisation"
              value={values.organisationId}
              onChange={(o) => {
                setFieldValue('organisationId', o.value);
                setTimeout(() => setFieldTouched('organisationId', true));
              }}
              onBlur={handleBlur('organisationId')}
              error={touched.organisationId && Boolean(errors.organisationId)}
              helperText={touched.organisationId && errors.organisationId}
              isSearchable
            />
            <Spacer $px={20} />
            <InputLabel
              tooltip={`<div style="font-size: 12px;padding: 6px 0; max-width: 320px;"><b>Open:</b> (All users within the organisation have automatic access)<br /><br /><b>Restricted:</b> (Users within the organisation must be invited to join the workspace)</div>`}
            >
              WORKSPACE RESTRICTION *
            </InputLabel>
            <RestrictAccessSelect
              value={values.isRestricted}
              onChange={(val) => {
                setFieldValue('isRestricted', val.value);
              }}
              onBlur={handleBlur('isRestricted')}
              error={touched.isRestricted && Boolean(errors.isRestricted)}
              helperText={touched.isRestricted && errors.isRestricted}
            />
            <Spacer $px={20} />
            <InputLabel>ENVIRONMENT TYPE *</InputLabel>
            <WorkspaceTypeSelect
              value={values.plan}
              onChange={(o) => {
                setFieldValue('plan', o.value);
                setTimeout(() => setFieldTouched('plan', true));
              }}
              placeholder="Environment type"
              onBlur={handleBlur('plan')}
              error={touched.plan && Boolean(errors.plan)}
              helperText={touched.plan && errors.plan}
            />
            <Spacer $px={20} />
            <InputLabel>CURRENCY</InputLabel>
            <MaterialInput
              id="currency"
              placeholder="Currency"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.currency}
              fullWidth
              error={touched.currency && Boolean(errors.currency)}
              helperText={touched.currency && errors.currency}
            />
            <Spacer $px={40} />
            <div className="btns">
              <Button secondary onClick={() => setIsCloseRequested(true)} primary>
                CANCEL
              </Button>
              <Button
                primary
                disabled={!isValid || isSubmitting || !dirty || isLoadingSwitch}
                onClick={() => handleSubmit()}
              >
                CREATE
              </Button>
            </div>

            {!!err && (
              <>
                <Spacer $px={15} />
                <div className="error">{err}</div>
              </>
            )}
          </div>

          <Spacer $px={40} />
        </StyledModal>
      }
    />
  );
};
